import React, { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '../api/client';
import { PRIMARY, TEXT_DARK, TEXT_MUTED, BORDER_LIGHT } from '../components/legacy-ball/table-management/designTokens';

// --- Types ---

interface GuestCredential {
  fullName: string;
  ticketNumber: string;
  hasSetPassword: boolean;
}

interface LookupResponse {
  purchaserName: string;
  referenceId: string;
  ticketCount: number;
  guests: GuestCredential[];
}

interface ResetResponse {
  success: boolean;
  resetCount: number;
  errors: string[];
}

// --- Styles ---

const styles: Record<string, React.CSSProperties> = {
  title: {
    color: PRIMARY,
    marginBottom: '0.5rem',
    fontSize: '1.5rem',
    textAlign: 'center' as const,
  },
  subtitle: {
    color: '#666',
    marginBottom: '1.5rem',
    fontSize: '0.9rem',
    textAlign: 'center' as const,
  },
  field: {
    marginBottom: '1rem',
  },
  label: {
    display: 'block',
    marginBottom: '0.25rem',
    fontWeight: 600,
    fontSize: '0.85rem',
    color: '#333',
  },
  input: {
    width: '100%',
    padding: '0.6rem 0.75rem',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '4px',
    fontSize: '1rem',
    boxSizing: 'border-box' as const,
  },
  inputError: {
    borderColor: '#d32f2f',
  },
  button: {
    width: '100%',
    padding: '0.75rem',
    backgroundColor: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: '0.5rem',
  },
  link: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    minHeight: '44px',
    textAlign: 'center' as const,
    marginTop: '1rem',
    color: PRIMARY,
    fontSize: '0.85rem',
    boxSizing: 'border-box' as const,
  },
  error: {
    color: '#d32f2f',
    backgroundColor: '#fdecea',
    padding: '0.5rem 0.75rem',
    borderRadius: '4px',
    marginBottom: '1rem',
    fontSize: '0.85rem',
  },
  fieldError: {
    color: '#d32f2f',
    fontSize: '0.8rem',
    marginTop: '0.25rem',
  },
  success: {
    color: '#2e7d32',
    backgroundColor: '#e8f5e9',
    padding: '0.5rem 0.75rem',
    borderRadius: '4px',
    marginBottom: '1rem',
    fontSize: '0.85rem',
  },
  spinner: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '2rem',
    color: TEXT_MUTED,
    fontSize: '0.9rem',
  },
  summaryCard: {
    backgroundColor: '#f9fafb',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '6px',
    padding: '1rem',
    marginBottom: '1rem',
  },
  summaryTitle: {
    margin: '0 0 0.5rem 0',
    fontSize: '1rem',
    fontWeight: 600,
    color: TEXT_DARK,
  },
  summaryDetail: {
    margin: '0.25rem 0',
    fontSize: '0.85rem',
    color: TEXT_MUTED,
  },
  guestList: {
    listStyle: 'none',
    padding: 0,
    margin: '0 0 1rem 0',
  },
  guestItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.6rem 0',
    borderBottom: `1px solid ${BORDER_LIGHT}`,
    flexWrap: 'wrap' as const,
    gap: '0.25rem',
  },
  guestInfo: {
    flex: 1,
    minWidth: 0,
  },
  guestName: {
    fontSize: '0.9rem',
    fontWeight: 600,
    color: TEXT_DARK,
  },
  guestTicket: {
    fontSize: '0.8rem',
    color: TEXT_MUTED,
    fontFamily: 'monospace',
  },
  badgeFirstTime: {
    display: 'inline-block',
    fontSize: '0.7rem',
    fontWeight: 600,
    padding: '0.15rem 0.5rem',
    borderRadius: '10px',
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
    whiteSpace: 'nowrap' as const,
  },
  badgePasswordSet: {
    display: 'inline-block',
    fontSize: '0.7rem',
    fontWeight: 600,
    padding: '0.15rem 0.5rem',
    borderRadius: '10px',
    backgroundColor: '#f0f0f0',
    color: '#666',
    whiteSpace: 'nowrap' as const,
  },
  instructions: {
    backgroundColor: '#fffde7',
    border: '1px solid #fff9c4',
    borderRadius: '6px',
    padding: '0.75rem 1rem',
    marginBottom: '1rem',
    fontSize: '0.82rem',
    color: '#5d4037',
    lineHeight: 1.5,
  },
  resetButton: {
    width: '100%',
    padding: '0.65rem',
    backgroundColor: '#fff',
    color: '#d32f2f',
    border: '1px solid #d32f2f',
    borderRadius: '4px',
    fontSize: '0.9rem',
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: '0.5rem',
  },
  signInLink: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    minHeight: '44px',
    textAlign: 'center' as const,
    marginTop: '0.75rem',
    padding: '0.65rem',
    backgroundColor: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.95rem',
    fontWeight: 600,
    textDecoration: 'none',
    boxSizing: 'border-box' as const,
  },
  dialogOverlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 9999,
    padding: '1rem',
  },
  dialogCard: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '1.5rem',
    maxWidth: '380px',
    width: '100%',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
  },
  dialogTitle: {
    margin: '0 0 0.75rem 0',
    fontSize: '1.1rem',
    fontWeight: 600,
    color: TEXT_DARK,
  },
  dialogText: {
    margin: '0 0 1.25rem 0',
    fontSize: '0.85rem',
    color: TEXT_MUTED,
    lineHeight: 1.5,
  },
  dialogActions: {
    display: 'flex',
    gap: '0.75rem',
    justifyContent: 'flex-end',
  },
  dialogCancel: {
    padding: '0.5rem 1rem',
    backgroundColor: '#e0e0e0',
    color: '#333',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  dialogConfirm: {
    padding: '0.5rem 1rem',
    backgroundColor: '#d32f2f',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
};

// --- Component ---

export function GetCredentialsPage() {
  // Form state
  const [referenceId, setReferenceId] = useState('');
  const [lastName, setLastName] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{ referenceId?: string; lastName?: string }>({});

  // API state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<LookupResponse | null>(null);

  // Reset state
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetMessage, setResetMessage] = useState('');
  const [resetError, setResetError] = useState('');

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setResetMessage('');
    setResetError('');

    // Validate
    const errors: { referenceId?: string; lastName?: string } = {};
    if (!referenceId.trim()) {
      errors.referenceId = 'Order Reference ID is required';
    }
    if (!lastName.trim()) {
      errors.lastName = 'Last Name is required';
    }
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    setLoading(true);

    try {
      const response = await apiClient<LookupResponse>('/credentials/lookup', {
        method: 'POST',
        body: { referenceId: referenceId.trim(), lastName: lastName.trim() },
      });
      setResult(response);
      setError('');
    } catch (err: unknown) {
      const apiError = err as { statusCode?: number; message?: string };
      if (apiError.statusCode === 404) {
        setError('No order found matching that Order ID and last name. Please check your confirmation email and try again.');
      } else if (apiError.statusCode === 429) {
        setError('Too many attempts. Please wait 15 minutes before trying again.');
      } else {
        setError('Something went wrong. Please try again later.');
      }
      setResult(null);
    } finally {
      setLoading(false);
    }
  }

  async function handleResetPasswords() {
    setShowResetDialog(false);
    setResetting(true);
    setResetMessage('');
    setResetError('');

    try {
      const response = await apiClient<ResetResponse>('/credentials/reset-passwords', {
        method: 'POST',
        body: { referenceId: referenceId.trim(), lastName: lastName.trim() },
      });
      if (response.success) {
        setResetMessage(
          `All ${response.resetCount} password${response.resetCount !== 1 ? 's have' : ' has'} been reset. Guests can now log in using their ticket numbers.`
        );
      }
      if (response.errors && response.errors.length > 0) {
        setResetError(`Some accounts could not be reset: ${response.errors.join(', ')}`);
      }
    } catch (err: unknown) {
      const apiError = err as { statusCode?: number; message?: string };
      if (apiError.statusCode === 429) {
        setResetError('Too many attempts. Please wait 15 minutes before trying again.');
      } else {
        setResetError('Password reset failed. Please try again later.');
      }
    } finally {
      setResetting(false);
    }
  }

  // --- Render ---

  return (
    <div className="login-page attendee-page">
      <div className="login-page__card">
        <h1 style={styles.title}>KDU Georgia 2026</h1>
        <img
          src="/KDULogo.PNG"
          alt="KDU"
          style={{ width: '80px', height: '80px', objectFit: 'contain', margin: '0 auto 12px', display: 'block', borderRadius: '12px' }}
        />
        <p style={styles.subtitle}>Get My Credentials</p>

        {/* Error messages */}
        {error && <div style={styles.error}>{error}</div>}
        {resetMessage && <div style={styles.success}>{resetMessage}</div>}
        {resetError && <div style={styles.error}>{resetError}</div>}

        {/* Loading state */}
        {(loading || resetting) && (
          <div style={styles.spinner} aria-live="polite">
            <span>⏳ {resetting ? 'Resetting passwords...' : 'Looking up your order...'}</span>
          </div>
        )}

        {/* Form (visible when no results and not loading) */}
        {!result && !loading && (
          <form onSubmit={handleSubmit} noValidate>
            <div style={styles.field}>
              <label style={styles.label} htmlFor="referenceId">Order Reference ID</label>
              <input
                id="referenceId"
                type="text"
                style={{ ...styles.input, ...(fieldErrors.referenceId ? styles.inputError : {}) }}
                value={referenceId}
                onChange={(e) => { setReferenceId(e.target.value); setFieldErrors((prev) => ({ ...prev, referenceId: undefined })); }}
                placeholder="e.g. ORD-2026-001"
                aria-describedby={fieldErrors.referenceId ? 'referenceId-error' : undefined}
              />
              {fieldErrors.referenceId && (
                <p id="referenceId-error" style={styles.fieldError} role="alert">{fieldErrors.referenceId}</p>
              )}
            </div>

            <div style={styles.field}>
              <label style={styles.label} htmlFor="lastName">Last Name</label>
              <input
                id="lastName"
                type="text"
                style={{ ...styles.input, ...(fieldErrors.lastName ? styles.inputError : {}) }}
                value={lastName}
                onChange={(e) => { setLastName(e.target.value); setFieldErrors((prev) => ({ ...prev, lastName: undefined })); }}
                placeholder="As shown on your order"
                aria-describedby={fieldErrors.lastName ? 'lastName-error' : undefined}
              />
              {fieldErrors.lastName && (
                <p id="lastName-error" style={styles.fieldError} role="alert">{fieldErrors.lastName}</p>
              )}
            </div>

            <button type="submit" style={styles.button}>
              Look Up My Order
            </button>
          </form>
        )}

        {/* Results display */}
        {result && !loading && (
          <div>
            {/* Order Summary */}
            <div style={styles.summaryCard}>
              <h2 style={styles.summaryTitle}>Order Summary</h2>
              <p style={styles.summaryDetail}><strong>Purchaser:</strong> {result.purchaserName}</p>
              <p style={styles.summaryDetail}><strong>Reference ID:</strong> {result.referenceId}</p>
              <p style={styles.summaryDetail}><strong>Tickets:</strong> {result.ticketCount}</p>
            </div>

            {/* Guest List */}
            <h3 style={{ fontSize: '0.95rem', fontWeight: 600, color: TEXT_DARK, marginBottom: '0.5rem' }}>
              Guest Credentials
            </h3>
            <ul style={styles.guestList}>
              {result.guests.map((guest) => (
                <li key={guest.ticketNumber} style={styles.guestItem}>
                  <div style={styles.guestInfo}>
                    <div style={styles.guestName}>{guest.fullName}</div>
                    <div style={styles.guestTicket}>{guest.ticketNumber}</div>
                  </div>
                  <span style={guest.hasSetPassword ? styles.badgePasswordSet : styles.badgeFirstTime}>
                    {guest.hasSetPassword ? 'Password set' : 'First-time login'}
                  </span>
                </li>
              ))}
            </ul>

            {/* Instructions */}
            <div style={styles.instructions}>
              <strong>First-time login instructions:</strong><br />
              Your ticket number (e.g., TKT-042) is your username AND your initial password.
              On first login, you'll be asked to set a new password.
            </div>

            {/* Reset All Passwords */}
            <button
              type="button"
              style={styles.resetButton}
              onClick={() => setShowResetDialog(true)}
              disabled={resetting}
            >
              Reset All Passwords
            </button>

            {/* Sign In Now */}
            <Link to="/login" style={styles.signInLink}>
              Sign In Now
            </Link>
          </div>
        )}

        {/* Back to Login link (always visible) */}
        <Link to="/login" style={styles.link}>
          ← Back to Login
        </Link>
      </div>

      {/* Confirmation Dialog */}
      {showResetDialog && (
        <div style={styles.dialogOverlay} onClick={() => setShowResetDialog(false)}>
          <div style={styles.dialogCard} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="reset-dialog-title">
            <h2 id="reset-dialog-title" style={styles.dialogTitle}>Reset All Passwords?</h2>
            <p style={styles.dialogText}>
              This will reset all guest passwords in this order back to their ticket numbers.
              Guests will be required to set a new password on their next login.
            </p>
            <div style={styles.dialogActions}>
              <button type="button" style={styles.dialogCancel} onClick={() => setShowResetDialog(false)}>
                Cancel
              </button>
              <button type="button" style={styles.dialogConfirm} onClick={handleResetPasswords}>
                Reset Passwords
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
